import React, { useRef, useState, useMemo, useCallback, useEffect } from 'react';
import {
  Plus, Clock, ChevronRight, ChevronDown, X, Search, Star,
  Inbox, Mail, MailOpen, Filter, SlidersHorizontal, Tag,
  ArrowUpRight, ArrowDownLeft, Phone, MessageSquare, Video,
  Users, UserCheck, AtSign, ArrowRight, Send, Lock, Eye,
  Calendar, Hash, MessageCircle, ChevronUp, UserPlus, Bell,
  Paperclip, FileText, Image, Film, Download, Trash2,
} from 'lucide-react';
import useArrowNav from '../../hooks/useArrowNav';
import {
  safeAsync, Badge, emptyMsg, cardWrap, btnPrimary, btnSm, neuShadow, neuShadowSm,
  neuShadowInset, inp, CHANNEL_COLORS,
} from './crmShared';

/* ═══════════════════════════════════════════════════════════════════════════
   InteractionsTab — Team Inbox with Conversations
   • Inbox tabs: All, Unread, Recent, Starred, Assigned to Me, Followed by Me
   • Enhanced search with date range, contact lookup, tag filters
   • Advanced filter panel with 7 filter dimensions
   • Secure Internal Team Chat panel
   • Threaded notes/comments per conversation
   • Collapsible accordion grouped by interaction_type
   ═══════════════════════════════════════════════════════════════════════════ */

/* ─── Interaction type icons / colors ─────────────────────────────────── */
const TYPE_META = {
  'Phone Call':    { icon: Phone,          color: '#F26522' },
  'Email':         { icon: Mail,           color: '#2E8B98' },
  'In Person':     { icon: Users,          color: '#62BB46' },
  'Video Call':    { icon: Video,          color: '#256F7A' },
  'Text Message':  { icon: MessageSquare,  color: '#4FA038' },
  'Other':         { icon: Inbox,          color: '#6C757D' },
};

/* ─── Filter option sets ──────────────────────────────────────────────── */
const FILTER_DEFS = [
  { key: 'assigned',       label: 'Assigned',                icon: UserCheck,      options: [] },
  { key: 'follower',       label: 'Follower',                icon: Users,          options: [] },
  { key: 'mentioned',      label: 'Mentioned',               icon: AtSign,         options: [] },
  { key: 'lastDirection',  label: 'Last Message Direction',   icon: ArrowRight,     options: ['Inbound', 'Outbound'] },
  { key: 'lastOutbound',   label: 'Last Outbound Msg Type',  icon: ArrowUpRight,   options: ['Phone Call', 'Email', 'Text Message', 'Video Call', 'In Person', 'Other'] },
  { key: 'lastChannel',    label: 'Last Message Channel',    icon: MessageSquare,  options: ['SMS', 'Email', 'WhatsApp', 'In-App', 'Voice'] },
  { key: 'tagged',         label: 'Tagged',                  icon: Tag,            options: [] },
];

export default function InteractionsTab({
  interactions, canCreate, setShowLogInteraction,
  allUsers, tags, contacts, onContactClick,
}) {
  // ── Session / current user ────────────────────────────────────────────
  const [currentUser, setCurrentUser] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const sess = await window.zignagenixAPI.getSession();
        setCurrentUser(sess?.user || sess);
      } catch {}
    })();
  }, []);

  // ── Inbox tab state ──────────────────────────────────────────────────────
  const [inboxTab, setInboxTab] = useState('all');
  const [openDropdown, setOpenDropdown] = useState(null);

  // ── Client-side read/star state (persisted in localStorage) ──────────
  const [readSet, setReadSet] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('crm_ix_read') || '[]')); }
    catch { return new Set(); }
  });
  const [starSet, setStarSet] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('crm_ix_star') || '[]')); }
    catch { return new Set(); }
  });

  useEffect(() => { localStorage.setItem('crm_ix_read', JSON.stringify([...readSet])); }, [readSet]);
  useEffect(() => { localStorage.setItem('crm_ix_star', JSON.stringify([...starSet])); }, [starSet]);

  const toggleRead = useCallback((id) => {
    setReadSet(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }, []);
  const toggleStar = useCallback((id) => {
    setStarSet(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }, []);
  const markAllRead = useCallback(() => {
    setReadSet(new Set(interactions.map(ix => ix.id)));
  }, [interactions]);

  // ── Followers state (from server) ────────────────────────────────────
  const [followedIds, setFollowedIds] = useState(new Set());
  useEffect(() => {
    (async () => {
      try {
        const rows = await window.zignagenixAPI.getCRMInteractionFollowers();
        if (Array.isArray(rows)) setFollowedIds(new Set(rows.map(r => r.interaction_id)));
      } catch {}
    })();
  }, []);

  const toggleFollow = useCallback(async (id) => {
    try {
      const res = await window.zignagenixAPI.toggleCRMInteractionFollower(id);
      setFollowedIds(prev => {
        const n = new Set(prev);
        if (res.following) n.add(id); else n.delete(id);
        return n;
      });
    } catch {}
  }, []);

  // ── Search state (enhanced) ────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [contactSearch, setContactSearch] = useState('');
  const [tagSearch, setTagSearch] = useState('');
  const [showEnhancedSearch, setShowEnhancedSearch] = useState(false);

  // ── Filter panel state ──────────────────────────────────────────────────
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    assigned: '', follower: '', mentioned: '',
    lastDirection: '', lastOutbound: '', lastChannel: '', tagged: '',
  });
  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  const clearFilters = () => setFilters({
    assigned: '', follower: '', mentioned: '',
    lastDirection: '', lastOutbound: '', lastChannel: '', tagged: '',
  });

  const setFilter = (key, value) => setFilters(f => ({ ...f, [key]: value }));

  // ── Chat panel state ──────────────────────────────────────────────────
  const [showChat, setShowChat] = useState(false);
  const [chatChannels, setChatChannels] = useState([]);
  const [activeChannel, setActiveChannel] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef(null);
  const chatPollRef = useRef(null);

  // Create channel modal state
  const [showCreateChannel, setShowCreateChannel] = useState(false);
  const [newChannelName, setNewChannelName] = useState('');
  const [newChannelType, setNewChannelType] = useState('direct'); // 'direct' or 'group'
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [createLoading, setCreateLoading] = useState(false);

  // Delete channel state
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');

  // Attachment state
  const [pendingAttachments, setPendingAttachments] = useState([]); // files selected but not yet sent
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [channelAttachments, setChannelAttachments] = useState({}); // { messageId: [attachment, ...] }
  const fileInputRef = useRef(null);

  // Load chat channels
  const loadChatChannels = useCallback(async () => {
    try {
      const channels = await window.zignagenixAPI.getChatChannels();
      if (Array.isArray(channels)) setChatChannels(channels);
    } catch {}
  }, []);

  useEffect(() => {
    if (showChat) loadChatChannels();
  }, [showChat, loadChatChannels]);

  // Load messages for active channel
  const loadChatMessages = useCallback(async () => {
    if (!activeChannel) return;
    try {
      const msgs = await window.zignagenixAPI.getChatMessages(activeChannel.id);
      if (Array.isArray(msgs)) setChatMessages(msgs);
    } catch {}
  }, [activeChannel]);

  useEffect(() => {
    if (activeChannel) {
      loadChatMessages();
      chatPollRef.current = setInterval(loadChatMessages, 5000);
      return () => clearInterval(chatPollRef.current);
    }
  }, [activeChannel, loadChatMessages]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const sendChatMessage = useCallback(async () => {
    if (!chatInput.trim() || !activeChannel) return;
    setChatLoading(true);
    try {
      await window.zignagenixAPI.sendChatMessage(activeChannel.id, chatInput.trim(), false, 'normal');
      setChatInput('');
      await loadChatMessages();
    } catch {} finally { setChatLoading(false); }
  }, [chatInput, activeChannel, loadChatMessages]);

  // ── File Attachment Helpers ──────────────────────────────────────────────
  // Uses Electron's native dialog.showOpenDialog() via IPC for sandbox compatibility.
  // Falls back to HTML file input for web (non-Electron) deployments.
  const isElectron = !!window.zignagenixAPI?.selectFiles;

  const handleFileSelect = useCallback(async (e) => {
    if (isElectron) {
      // Native Electron dialog — files come back as { filename, mimeType, size, base64 }
      try {
        const result = await window.zignagenixAPI.selectFiles({ maxFileSize: 100 * 1024 * 1024 });
        if (result?.error) { alert(result.message); return; }
        if (result?.canceled || !result?.files?.length) return;
        setPendingAttachments(prev => [...prev, ...result.files]);
      } catch (err) {
        console.error('[Chat] File select error:', err);
      }
    } else {
      // Web fallback — standard File objects from <input type="file">
      const files = Array.from(e?.target?.files || []);
      const maxSize = 100 * 1024 * 1024;
      const valid = files.filter(f => f.size <= maxSize);
      if (valid.length < files.length) {
        alert(`${files.length - valid.length} file(s) exceed 100MB and were skipped.`);
      }
      // Convert to same shape as Electron for uniform handling
      const converted = [];
      for (const file of valid) {
        const base64 = await fileToBase64(file);
        converted.push({ filename: file.name, mimeType: file.type || 'application/octet-stream', size: file.size, base64 });
      }
      setPendingAttachments(prev => [...prev, ...converted]);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, []);

  const removePendingAttachment = useCallback((index) => {
    setPendingAttachments(prev => prev.filter((_, i) => i !== index));
  }, []);

  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]); // strip data:...;base64, prefix
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  // pendingAttachments are now always { filename, mimeType, size, base64 } objects
  const uploadAttachments = useCallback(async (channelId, files) => {
    const results = [];
    for (const file of files) {
      const res = await window.zignagenixAPI.uploadChatAttachment(
        channelId, file.base64, file.filename, file.mimeType, false
      );
      if (res && !res.error) results.push(res);
    }
    return results;
  }, []);

  // Toast notification state
  const [sendToast, setSendToast] = useState(null); // { type: 'success'|'error', message }

  const sendWithAttachments = useCallback(async () => {
    if ((!chatInput.trim() && pendingAttachments.length === 0) || !activeChannel) return;
    setChatLoading(true);
    const hasAttachments = pendingAttachments.length > 0;
    setUploadingFiles(hasAttachments);
    setSendToast(null);
    try {
      // Upload files first
      let attachmentIds = [];
      if (hasAttachments) {
        const uploaded = await uploadAttachments(activeChannel.id, pendingAttachments);
        attachmentIds = uploaded.map(a => a.attachmentId).filter(Boolean);
        if (attachmentIds.length === 0 && pendingAttachments.length > 0) {
          setSendToast({ type: 'error', message: 'File upload failed. Check S3 configuration.' });
          return;
        }
        if (attachmentIds.length < pendingAttachments.length) {
          setSendToast({ type: 'error', message: `${pendingAttachments.length - attachmentIds.length} file(s) failed to upload.` });
        }
      }
      // Send message (or just a placeholder if only attachments)
      const messageText = chatInput.trim() || (attachmentIds.length > 0 ? `📎 ${pendingAttachments.map(f => f.filename).join(', ')}` : '');
      if (messageText) {
        const res = await window.zignagenixAPI.sendChatMessage(activeChannel.id, messageText, false, 'normal');
        // Link attachments to the message
        if (res?.messageId && attachmentIds.length > 0) {
          await window.zignagenixAPI.linkChatAttachments(res.messageId, attachmentIds);
        }
      }
      setChatInput('');
      setPendingAttachments([]);
      // Refresh messages immediately, then attachments after a brief delay
      await loadChatMessages();
      // Small delay to let DB commit the link update before querying
      setTimeout(async () => {
        await loadChannelAttachments(activeChannel.id);
      }, 300);
      // Success toast
      if (hasAttachments && attachmentIds.length > 0) {
        const fileWord = attachmentIds.length === 1 ? 'file' : 'files';
        setSendToast({ type: 'success', message: `✓ ${attachmentIds.length} ${fileWord} sent securely` });
        setTimeout(() => setSendToast(null), 4000);
      }
    } catch (err) {
      console.error('[Chat] Send with attachments failed:', err);
      setSendToast({ type: 'error', message: `Send failed: ${err.message || 'Unknown error'}` });
      setTimeout(() => setSendToast(null), 6000);
    } finally {
      setChatLoading(false);
      setUploadingFiles(false);
    }
  }, [chatInput, activeChannel, pendingAttachments, uploadAttachments, loadChatMessages]);

  // Load attachments for active channel
  const loadChannelAttachments = useCallback(async (channelId) => {
    try {
      const atts = await window.zignagenixAPI.getChatAttachments(channelId);
      if (Array.isArray(atts)) {
        const byMessage = {};
        atts.forEach(a => {
          const key = a.message_id || 'unlinked';
          if (!byMessage[key]) byMessage[key] = [];
          byMessage[key].push(a);
        });
        setChannelAttachments(byMessage);
      }
    } catch {}
  }, []);

  useEffect(() => {
    if (activeChannel) loadChannelAttachments(activeChannel.id);
  }, [activeChannel, loadChannelAttachments, chatMessages]);

  const downloadAttachment = useCallback(async (attachmentId) => {
    try {
      const res = await window.zignagenixAPI.getChatAttachmentUrl(attachmentId);
      if (res?.url) window.open(res.url, '_blank');
    } catch {}
  }, []);

  const getFileIcon = (mimeType) => {
    if (!mimeType) return FileText;
    if (mimeType.startsWith('image/')) return Image;
    if (mimeType.startsWith('video/')) return Film;
    return FileText;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Create channel
  const createChannel = useCallback(async () => {
    if (!newChannelName.trim() || selectedMembers.length === 0) return;
    setCreateLoading(true);
    try {
      const memberIds = selectedMembers.map(m => m.id);
      await window.zignagenixAPI.createChatChannel(
        newChannelName.trim(),
        newChannelType,
        memberIds,
        null
      );
      setNewChannelName('');
      setNewChannelType('direct');
      setSelectedMembers([]);
      setMemberSearch('');
      setShowCreateChannel(false);
      await loadChatChannels();
    } catch (err) {
      alert('Failed to create channel: ' + (err.message || 'Unknown error'));
    } finally { setCreateLoading(false); }
  }, [newChannelName, newChannelType, selectedMembers, loadChatChannels]);

  // Delete channel
  const deleteChannel = useCallback(async (channelId) => {
    try {
      await window.zignagenixAPI.deleteChatChannel(channelId, deleteReason || 'Deleted by user');
      setDeleteConfirmId(null);
      setDeleteReason('');
      if (activeChannel?.id === channelId) {
        setActiveChannel(null);
        setChatMessages([]);
      }
      await loadChatChannels();
    } catch (err) {
      alert('Failed to delete channel: ' + (err.message || 'Unknown error'));
    }
  }, [deleteReason, activeChannel, loadChatChannels]);

  // Toggle member selection for channel creation
  const toggleMember = useCallback((user) => {
    setSelectedMembers(prev => {
      const exists = prev.find(m => m.id === user.id);
      if (exists) return prev.filter(m => m.id !== user.id);
      // For direct channels, only allow 1 member
      if (newChannelType === 'direct') return [user];
      return [...prev, user];
    });
  }, [newChannelType]);

  // Filtered user list for member search
  const filteredUsers = useMemo(() => {
    const list = (allUsers || []).filter(u => u.id !== currentUser?.id);
    if (!memberSearch.trim()) return list;
    const q = memberSearch.toLowerCase();
    return list.filter(u =>
      (u.full_name || u.display_name || u.username || '').toLowerCase().includes(q)
    );
  }, [allUsers, currentUser, memberSearch]);

  // ── Notes panel state ───────────────────────────────────────────────────
  const [expandedNoteId, setExpandedNoteId] = useState(null);
  const [notesMap, setNotesMap] = useState({});
  const [noteInput, setNoteInput] = useState('');

  const loadNotes = useCallback(async (interactionId) => {
    try {
      const notes = await window.zignagenixAPI.getCRMInteractionNotes(interactionId);
      if (Array.isArray(notes)) setNotesMap(prev => ({ ...prev, [interactionId]: notes }));
    } catch {}
  }, []);

  const addNote = useCallback(async (interactionId) => {
    if (!noteInput.trim()) return;
    try {
      await window.zignagenixAPI.createCRMInteractionNote(interactionId, noteInput.trim());
      setNoteInput('');
      await loadNotes(interactionId);
    } catch {}
  }, [noteInput, loadNotes]);

  // ── Build dynamic filter options ──────────────────────────────────────
  const userOptions = useMemo(() => (allUsers || []).map(u => u.full_name || u.username || u.display_name), [allUsers]);
  const tagOptions = useMemo(() => (tags || []).map(t => t.name || t.tag_name), [tags]);

  // ── Expanded type groups ──────────────────────────────────────────────
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  const toggleGroup = useCallback((type) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }, []);

  // ── Filter pipeline: inbox tab → search → enhanced search → advanced filters → group ────
  const processed = useMemo(() => {
    let list = [...interactions];
    const userId = currentUser?.id;

    // Inbox tab filter
    if (inboxTab === 'unread') list = list.filter(ix => !readSet.has(ix.id));
    else if (inboxTab === 'starred') list = list.filter(ix => starSet.has(ix.id));
    else if (inboxTab === 'recent') {
      const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 7);
      list = list.filter(ix => new Date(ix.interaction_date) >= cutoff);
    } else if (inboxTab === 'assigned' && userId) {
      list = list.filter(ix => ix.assigned_to === userId || ix.logged_by === userId);
    } else if (inboxTab === 'followed') {
      list = list.filter(ix => followedIds.has(ix.id));
    }

    // Basic text search
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(ix => {
        const name = [ix.contact_first, ix.contact_last].filter(Boolean).join(' ') ||
                     [ix.patient_first, ix.patient_last].filter(Boolean).join(' ');
        return (
          ix.subject?.toLowerCase().includes(q) ||
          name.toLowerCase().includes(q) ||
          ix.description?.toLowerCase().includes(q) ||
          ix.outcome?.toLowerCase().includes(q) ||
          ix.interaction_type?.toLowerCase().includes(q)
        );
      });
    }

    // Enhanced search: date range
    if (dateFrom) {
      list = list.filter(ix => ix.interaction_date?.substring(0, 10) >= dateFrom);
    }
    if (dateTo) {
      list = list.filter(ix => ix.interaction_date?.substring(0, 10) <= dateTo);
    }

    // Enhanced search: contact
    if (contactSearch.trim()) {
      const cq = contactSearch.toLowerCase();
      list = list.filter(ix => {
        const name = [ix.contact_first, ix.contact_last].filter(Boolean).join(' ') ||
                     [ix.patient_first, ix.patient_last].filter(Boolean).join(' ');
        return name.toLowerCase().includes(cq);
      });
    }

    // Enhanced search: tags
    if (tagSearch.trim()) {
      const tq = tagSearch.toLowerCase();
      list = list.filter(ix => (ix.tags || []).some(t => (t.name || t).toLowerCase().includes(tq)));
    }

    // Advanced filters
    if (filters.lastDirection) {
      list = list.filter(ix => ix.direction?.toLowerCase() === filters.lastDirection.toLowerCase());
    }
    if (filters.lastOutbound) {
      const t = filters.lastOutbound.toLowerCase().replace(/ /g, '_');
      list = list.filter(ix => ix.interaction_type === t && ix.direction === 'outbound');
    }
    if (filters.lastChannel) {
      const chMap = { sms: 'text_message', email: 'email', whatsapp: 'text_message', voice: 'phone_call', 'in-app': 'other' };
      const mapped = chMap[filters.lastChannel.toLowerCase()] || '';
      if (mapped) list = list.filter(ix => ix.interaction_type === mapped);
    }
    if (filters.assigned) {
      list = list.filter(ix => (ix.assigned_to_name || '').toLowerCase().includes(filters.assigned.toLowerCase()));
    }
    if (filters.tagged) {
      list = list.filter(ix => (ix.tags || []).some(t => (t.name || t).toLowerCase().includes(filters.tagged.toLowerCase())));
    }

    return list;
  }, [interactions, inboxTab, readSet, starSet, followedIds, currentUser, search, dateFrom, dateTo, contactSearch, tagSearch, filters]);

  // ── Group by interaction_type ─────────────────────────────────────────
  const grouped = useMemo(() => {
    const groups = {};
    processed.forEach(ix => {
      const type = (ix.interaction_type || 'other').replace(/_/g, ' ');
      const titleCased = type.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
      if (!groups[titleCased]) groups[titleCased] = [];
      groups[titleCased].push(ix);
    });
    const sortedKeys = Object.keys(groups).sort();
    const result = {};
    sortedKeys.forEach(k => { result[k] = groups[k]; });
    return result;
  }, [processed]);

  // ── Auto-expand on search ─────────────────────────────────────────────
  useEffect(() => {
    if (search && search.trim().length > 0) {
      setExpandedGroups(new Set(Object.keys(grouped)));
    }
  }, [search, grouped]);

  const expandAll = useCallback(() => setExpandedGroups(new Set(Object.keys(grouped))), [grouped]);
  const collapseAll = useCallback(() => setExpandedGroups(new Set()), []);

  // ── Counts ────────────────────────────────────────────────────────────
  const totalCount = interactions.length;
  const unreadCount = interactions.filter(ix => !readSet.has(ix.id)).length;
  const starredCount = interactions.filter(ix => starSet.has(ix.id)).length;
  const recentCutoff = new Date(); recentCutoff.setDate(recentCutoff.getDate() - 7);
  const recentCount = interactions.filter(ix => new Date(ix.interaction_date) >= recentCutoff).length;
  const assignedCount = currentUser ? interactions.filter(ix =>
    ix.assigned_to === currentUser.id || ix.logged_by === currentUser.id
  ).length : 0;
  const followedCount = interactions.filter(ix => followedIds.has(ix.id)).length;
  const filteredCount = processed.length;
  const typeNames = Object.keys(grouped);

  // ── Inbox tabs config ─────────────────────────────────────────────────
  const INBOX_TABS = [
    { id: 'all',      label: 'All',            icon: Inbox,    count: totalCount },
    { id: 'unread',   label: 'Unread',         icon: Mail,     count: unreadCount },
    { id: 'recent',   label: 'Recent',         icon: Clock,    count: recentCount },
    { id: 'starred',  label: 'Starred',        icon: Star,     count: starredCount },
    { id: 'assigned', label: 'Assigned to Me', icon: UserCheck, count: assignedCount },
    { id: 'followed', label: 'Followed by Me', icon: Eye,      count: followedCount },
  ];

  const hasEnhancedSearch = dateFrom || dateTo || contactSearch || tagSearch;

  return (
    <>
      {/* ═══ Team Inbox Header ═══ */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 14,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Inbox size={18} color="#2E8B98" />
          <span style={{ fontSize: 16, fontWeight: 700, color: '#212529' }}>Team Inbox</span>
          {unreadCount > 0 && (
            <span style={{
              background: '#DC3545', color: '#fff', fontSize: 10, fontWeight: 700,
              padding: '2px 7px', borderRadius: 10, minWidth: 18, textAlign: 'center',
            }}>{unreadCount}</span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {/* Team Chat toggle */}
          <button
            onClick={() => setShowChat(c => !c)}
            style={{
              ...btnSm, fontSize: 11,
              color: showChat ? '#fff' : '#2E8B98',
              background: showChat ? '#2E8B98' : '#e0f2fe',
              boxShadow: neuShadowSm,
            }}
          >
            <Lock size={12} /> <MessageCircle size={12} /> Team Chat
          </button>
          {unreadCount > 0 && (
            <button onClick={markAllRead} style={{
              ...btnSm, fontSize: 11, color: '#2E8B98', background: '#e0f2fe',
              boxShadow: neuShadowSm,
            }}>
              <MailOpen size={12} /> Mark All Read
            </button>
          )}
          {canCreate && (
            <button onClick={() => setShowLogInteraction(true)}
              style={{ ...btnPrimary, background: '#62BB46' }}>
              <Plus size={14} /> Log Conversation
            </button>
          )}
        </div>
      </div>

      {/* ═══ Secure Team Chat Panel (collapsible) ═══ */}
      <div style={{
        maxHeight: showChat ? 500 : 0, overflow: 'hidden',
        transition: 'max-height 0.3s ease-in-out',
      }}>
        <div style={{
          background: '#F8F9FA', borderRadius: 12, padding: 0,
          marginBottom: 12, boxShadow: neuShadowSm,
          border: '2px solid #2E8B98',
          display: 'flex', height: showCreateChannel ? 460 : 400,
          transition: 'height 0.2s ease',
        }}>
          {/* ─── Channel Sidebar ─── */}
          <div style={{
            width: 220, borderRight: '1px solid #e9ecef', overflowY: 'auto',
            background: '#f0f4f8', borderRadius: '12px 0 0 12px',
            display: 'flex', flexDirection: 'column',
          }}>
            {/* Sidebar header + New Channel button */}
            <div style={{
              padding: '10px 12px', borderBottom: '1px solid #e9ecef',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <Lock size={12} color="#2E8B98" />
              <span style={{ fontSize: 12, fontWeight: 700, color: '#212529', flex: 1 }}>Secure Channels</span>
              <button
                onClick={() => setShowCreateChannel(s => !s)}
                title="New Channel"
                style={{
                  background: showCreateChannel ? '#2E8B98' : 'none', border: 'none',
                  cursor: 'pointer', padding: 3, borderRadius: 6, display: 'flex',
                  color: showCreateChannel ? '#fff' : '#2E8B98',
                }}
              >
                <Plus size={14} />
              </button>
            </div>

            {/* Channel list */}
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {chatChannels.length === 0 && !showCreateChannel && (
                <div style={{ padding: 16, fontSize: 11, color: '#ADB5BD', textAlign: 'center' }}>
                  <MessageCircle size={20} color="#DEE2E6" style={{ marginBottom: 6 }} />
                  <div>No channels yet</div>
                  <button onClick={() => setShowCreateChannel(true)} style={{
                    marginTop: 8, ...btnSm, fontSize: 10, color: '#2E8B98',
                    background: '#e0f2fe', boxShadow: neuShadowSm,
                  }}>
                    <Plus size={11} /> Create Channel
                  </button>
                </div>
              )}
              {chatChannels.map(ch => {
                const isActive = activeChannel?.id === ch.id;
                const isDeleting = deleteConfirmId === ch.id;
                const typeIcon = ch.channel_type === 'direct' ? Users : Hash;
                const TypeIcon = typeIcon;
                return (
                  <div key={ch.id}>
                    <div
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        width: '100%', padding: '8px 10px', position: 'relative',
                        background: isActive ? '#dce8f0' : 'transparent',
                        borderLeft: isActive ? '3px solid #2E8B98' : '3px solid transparent',
                        transition: 'all 0.1s ease',
                      }}
                      onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#e8edf2'; }}
                      onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = isActive ? '#dce8f0' : 'transparent'; }}
                    >
                      <button
                        onClick={() => { setActiveChannel(ch); setDeleteConfirmId(null); }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 6, flex: 1,
                          background: 'none', border: 'none', cursor: 'pointer',
                          textAlign: 'left', fontSize: 12, color: '#212529', padding: 0,
                          minWidth: 0,
                        }}
                      >
                        <TypeIcon size={12} color={ch.channel_type === 'direct' ? '#62BB46' : '#6C757D'} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            fontWeight: isActive ? 700 : 500,
                            color: isActive ? '#2E8B98' : '#212529',
                          }}>
                            {ch.name || `Channel ${ch.id}`}
                          </div>
                          <div style={{ fontSize: 9, color: '#ADB5BD' }}>
                            {ch.channel_type === 'direct' ? 'Direct' : 'Group'}
                            {ch.member_count ? ` · ${ch.member_count} members` : ''}
                          </div>
                        </div>
                      </button>
                      {ch.unread_count > 0 && (
                        <span style={{
                          fontSize: 9, fontWeight: 700, background: '#DC3545', color: '#fff',
                          padding: '1px 5px', borderRadius: 8, flexShrink: 0,
                        }}>{ch.unread_count}</span>
                      )}
                      <button
                        onClick={e => { e.stopPropagation(); setDeleteConfirmId(isDeleting ? null : ch.id); }}
                        title="Delete channel"
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          padding: 2, flexShrink: 0, opacity: 0.4,
                        }}
                        onMouseEnter={e => { e.currentTarget.style.opacity = 1; }}
                        onMouseLeave={e => { e.currentTarget.style.opacity = 0.4; }}
                      >
                        <X size={12} color="#DC3545" />
                      </button>
                    </div>
                    {/* Delete confirmation inline */}
                    {isDeleting && (
                      <div style={{
                        padding: '6px 10px', background: '#fff5f5', borderLeft: '3px solid #DC3545',
                        fontSize: 10,
                      }}>
                        <div style={{ fontWeight: 700, color: '#DC3545', marginBottom: 4 }}>
                          Delete this channel?
                        </div>
                        <input
                          value={deleteReason}
                          onChange={e => setDeleteReason(e.target.value)}
                          placeholder="Reason (optional)..."
                          style={{ ...inp, fontSize: 10, padding: '4px 8px', marginBottom: 4 }}
                        />
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button onClick={() => deleteChannel(ch.id)} style={{
                            padding: '3px 8px', borderRadius: 6, border: 'none',
                            background: '#DC3545', color: '#fff', fontSize: 10,
                            fontWeight: 600, cursor: 'pointer',
                          }}>Delete</button>
                          <button onClick={() => { setDeleteConfirmId(null); setDeleteReason(''); }} style={{
                            padding: '3px 8px', borderRadius: 6, border: '1px solid #DEE2E6',
                            background: '#fff', color: '#6C757D', fontSize: 10,
                            fontWeight: 500, cursor: 'pointer',
                          }}>Cancel</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* ─── Main Area: Create Channel form OR Messages ─── */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {showCreateChannel ? (
              /* ── Create Channel Form ── */
              <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  marginBottom: 14,
                }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: '#212529', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Plus size={14} color="#2E8B98" /> New Secure Channel
                  </span>
                  <button onClick={() => setShowCreateChannel(false)} style={{
                    background: 'none', border: 'none', cursor: 'pointer', padding: 2,
                  }}>
                    <X size={16} color="#6C757D" />
                  </button>
                </div>

                {/* Channel type selector */}
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 10, fontWeight: 600, color: '#6C757D', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4, display: 'block' }}>
                    Channel Type
                  </label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {[
                      { id: 'direct', label: 'Direct Message', desc: 'One-to-one', icon: Users },
                      { id: 'group', label: 'Group Channel', desc: 'One-to-many', icon: Hash },
                    ].map(ct => (
                      <button key={ct.id} onClick={() => {
                        setNewChannelType(ct.id);
                        if (ct.id === 'direct' && selectedMembers.length > 1) setSelectedMembers([selectedMembers[0]]);
                      }} style={{
                        flex: 1, padding: '10px 12px', borderRadius: 10, border: 'none',
                        cursor: 'pointer', textAlign: 'left',
                        background: newChannelType === ct.id ? '#e0f2fe' : '#F8F9FA',
                        boxShadow: newChannelType === ct.id ? neuShadowSm : 'none',
                        border: newChannelType === ct.id ? '2px solid #2E8B98' : '2px solid transparent',
                        transition: 'all 0.15s ease',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                          <ct.icon size={14} color={newChannelType === ct.id ? '#2E8B98' : '#6C757D'} />
                          <span style={{ fontSize: 12, fontWeight: 700, color: newChannelType === ct.id ? '#2E8B98' : '#212529' }}>
                            {ct.label}
                          </span>
                        </div>
                        <div style={{ fontSize: 10, color: '#ADB5BD' }}>{ct.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Channel name */}
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 10, fontWeight: 600, color: '#6C757D', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4, display: 'block' }}>
                    Channel Name
                  </label>
                  <input
                    value={newChannelName}
                    onChange={e => setNewChannelName(e.target.value)}
                    placeholder={newChannelType === 'direct' ? 'e.g., Chat with Dr. Smith' : 'e.g., Patient Care Team'}
                    style={{ ...inp, fontSize: 12 }}
                  />
                </div>

                {/* Member selection */}
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 10, fontWeight: 600, color: '#6C757D', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4, display: 'block' }}>
                    {newChannelType === 'direct' ? 'Select Member' : 'Select Members'}
                    {selectedMembers.length > 0 && (
                      <span style={{ color: '#2E8B98', marginLeft: 6, fontWeight: 700 }}>
                        ({selectedMembers.length} selected)
                      </span>
                    )}
                  </label>

                  {/* Selected members chips */}
                  {selectedMembers.length > 0 && (
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 6 }}>
                      {selectedMembers.map(m => (
                        <span key={m.id} style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          padding: '3px 8px', borderRadius: 12, fontSize: 10, fontWeight: 600,
                          background: '#2E8B98', color: '#fff',
                        }}>
                          {m.full_name || m.display_name || m.username}
                          <button onClick={() => toggleMember(m)} style={{
                            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                            display: 'flex',
                          }}>
                            <X size={10} color="#fff" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Member search */}
                  <div style={{ position: 'relative', marginBottom: 6 }}>
                    <Search size={12} style={{ position: 'absolute', left: 8, top: 8, color: '#ADB5BD' }} />
                    <input
                      value={memberSearch}
                      onChange={e => setMemberSearch(e.target.value)}
                      placeholder="Search team members..."
                      style={{ ...inp, fontSize: 11, paddingLeft: 26, padding: '6px 10px 6px 26px' }}
                    />
                  </div>

                  {/* User list */}
                  <div style={{
                    maxHeight: 120, overflowY: 'auto', borderRadius: 8,
                    background: '#e0e5ec', boxShadow: neuShadowInset, padding: 4,
                  }}>
                    {filteredUsers.length === 0 && (
                      <div style={{ padding: 8, fontSize: 11, color: '#ADB5BD', textAlign: 'center' }}>
                        No team members found
                      </div>
                    )}
                    {filteredUsers.map(user => {
                      const isSelected = selectedMembers.some(m => m.id === user.id);
                      const name = user.full_name || user.display_name || user.username;
                      return (
                        <button
                          key={user.id}
                          onClick={() => toggleMember(user)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                            padding: '6px 10px', border: 'none', borderRadius: 6,
                            background: isSelected ? '#e0f2fe' : 'transparent',
                            cursor: 'pointer', textAlign: 'left',
                            transition: 'background 0.1s ease',
                          }}
                          onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#f0f4f8'; }}
                          onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = isSelected ? '#e0f2fe' : 'transparent'; }}
                        >
                          <div style={{
                            width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                            background: isSelected ? '#2E8B98' : '#ADB5BD',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: '#fff', fontSize: 10, fontWeight: 700,
                          }}>
                            {name.charAt(0).toUpperCase()}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 11, fontWeight: isSelected ? 700 : 500, color: isSelected ? '#2E8B98' : '#212529' }}>
                              {name}
                            </div>
                            {user.role && (
                              <div style={{ fontSize: 9, color: '#ADB5BD' }}>{user.role}</div>
                            )}
                          </div>
                          {isSelected && (
                            <UserCheck size={14} color="#2E8B98" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Create button */}
                <button
                  onClick={createChannel}
                  disabled={createLoading || !newChannelName.trim() || selectedMembers.length === 0}
                  style={{
                    ...btnPrimary, width: '100%', justifyContent: 'center',
                    opacity: (createLoading || !newChannelName.trim() || selectedMembers.length === 0) ? 0.5 : 1,
                  }}
                >
                  {createLoading ? 'Creating...' : (
                    <><Lock size={13} /> Create {newChannelType === 'direct' ? 'Direct Message' : 'Group Channel'}</>
                  )}
                </button>
              </div>
            ) : !activeChannel ? (
              /* ── Empty state ── */
              <div style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#ADB5BD', fontSize: 13,
              }}>
                <div style={{ textAlign: 'center' }}>
                  <MessageCircle size={32} color="#DEE2E6" style={{ marginBottom: 8 }} />
                  <div>Select a channel or create a new one</div>
                  <div style={{ fontSize: 11, marginTop: 4, color: '#CED4DA' }}>
                    <Lock size={10} /> End-to-end encrypted • HIPAA compliant
                  </div>
                  <button onClick={() => setShowCreateChannel(true)} style={{
                    marginTop: 12, ...btnPrimary, fontSize: 11,
                  }}>
                    <Plus size={13} /> New Channel
                  </button>
                </div>
              </div>
            ) : (
              /* ── Active Channel Messages ── */
              <>
                {/* Channel header */}
                <div style={{
                  padding: '8px 14px', borderBottom: '1px solid #e9ecef',
                  display: 'flex', alignItems: 'center', gap: 8,
                  background: '#fff', borderRadius: '0 12px 0 0',
                }}>
                  {activeChannel.channel_type === 'direct'
                    ? <Users size={14} color="#62BB46" />
                    : <Hash size={14} color="#2E8B98" />
                  }
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#212529' }}>
                      {activeChannel.name || `Channel ${activeChannel.id}`}
                    </span>
                    <span style={{ fontSize: 10, color: '#ADB5BD', marginLeft: 8 }}>
                      {activeChannel.channel_type === 'direct' ? 'Direct' : 'Group'}
                      {activeChannel.member_count ? ` · ${activeChannel.member_count} members` : ''}
                    </span>
                  </div>
                  <span style={{ fontSize: 10, color: '#ADB5BD', display: 'flex', alignItems: 'center', gap: 3 }}>
                    <Lock size={10} /> Secure
                  </span>
                </div>

                {/* Messages */}
                <div style={{
                  flex: 1, overflowY: 'auto', padding: '10px 14px',
                  display: 'flex', flexDirection: 'column', gap: 6,
                }}>
                  {chatMessages.length === 0 && (
                    <div style={{ textAlign: 'center', color: '#CED4DA', fontSize: 12, padding: 20 }}>
                      No messages yet — start the conversation
                    </div>
                  )}
                  {chatMessages.map(msg => {
                    const isMe = currentUser && msg.sender_id === currentUser.id;
                    return (
                      <div key={msg.id} style={{
                        alignSelf: isMe ? 'flex-end' : 'flex-start',
                        maxWidth: '70%',
                      }}>
                        {!isMe && (
                          <div style={{ fontSize: 10, color: '#6C757D', marginBottom: 2, fontWeight: 600 }}>
                            {msg.sender_name || 'User'}
                          </div>
                        )}
                        <div style={{
                          padding: '8px 12px', borderRadius: 12,
                          background: isMe ? '#2E8B98' : '#e0e5ec',
                          color: isMe ? '#fff' : '#212529',
                          fontSize: 12, lineHeight: 1.4,
                          boxShadow: isMe ? 'none' : neuShadowSm,
                        }}>
                          {msg.message_text}
                          {/* Inline attachment cards */}
                          {channelAttachments[msg.id] && channelAttachments[msg.id].map(att => {
                            const FIcon = getFileIcon(att.mime_type);
                            const isImage = att.mime_type?.startsWith('image/');
                            const isVideo = att.mime_type?.startsWith('video/');
                            return (
                              <div key={att.id} style={{
                                marginTop: 6, padding: '6px 8px', borderRadius: 8,
                                background: isMe ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.05)',
                                display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                              }} onClick={() => downloadAttachment(att.id)}>
                                <FIcon size={14} style={{ flexShrink: 0 }} />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: 11, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {att.original_filename}
                                  </div>
                                  <div style={{ fontSize: 9, opacity: 0.7 }}>
                                    {formatFileSize(att.file_size)} · {isImage ? 'Image' : isVideo ? 'Video' : 'File'}
                                  </div>
                                </div>
                                <Download size={12} style={{ flexShrink: 0, opacity: 0.7 }} />
                              </div>
                            );
                          })}
                        </div>
                        <div style={{
                          fontSize: 9, color: '#ADB5BD', marginTop: 2,
                          textAlign: isMe ? 'right' : 'left',
                          display: 'flex', alignItems: 'center', gap: 4,
                          justifyContent: isMe ? 'flex-end' : 'flex-start',
                        }}>
                          {msg.is_encrypted && <span title="AES-256 Encrypted (HIPAA Compliant)" style={{ color: '#28A745', fontSize: 10 }}>🔒</span>}
                          {msg.created_at ? new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                        </div>
                      </div>
                    );
                  })}
                  <div ref={chatEndRef} />
                </div>

                {/* Upload progress / Toast notification */}
                {uploadingFiles && (
                  <div style={{
                    padding: '8px 12px', borderTop: '1px solid #e9ecef',
                    background: '#EFF6FF', display: 'flex', alignItems: 'center', gap: 8,
                    fontSize: 12, color: '#1D4ED8',
                  }}>
                    <div style={{
                      width: 14, height: 14, border: '2px solid #1D4ED8', borderTopColor: 'transparent',
                      borderRadius: '50%', animation: 'spin 0.8s linear infinite',
                    }} />
                    Uploading and encrypting files securely...
                    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                  </div>
                )}
                {sendToast && !uploadingFiles && (
                  <div style={{
                    padding: '8px 12px', borderTop: '1px solid #e9ecef',
                    background: sendToast.type === 'success' ? '#F0FDF4' : '#FEF2F2',
                    color: sendToast.type === 'success' ? '#166534' : '#991B1B',
                    fontSize: 12, display: 'flex', alignItems: 'center', gap: 6,
                  }}>
                    {sendToast.message}
                    <button onClick={() => setSendToast(null)} style={{
                      background: 'none', border: 'none', cursor: 'pointer', marginLeft: 'auto',
                      color: 'inherit', padding: 0, display: 'flex',
                    }}>
                      <X size={12} />
                    </button>
                  </div>
                )}

                {/* Pending Attachments Preview */}
                {pendingAttachments.length > 0 && (
                  <div style={{
                    padding: '6px 12px', borderTop: '1px solid #e9ecef',
                    display: 'flex', gap: 6, flexWrap: 'wrap', background: '#f8f9fa',
                  }}>
                    {pendingAttachments.map((file, idx) => {
                      const FIcon = getFileIcon(file.mimeType);
                      return (
                        <div key={idx} style={{
                          display: 'flex', alignItems: 'center', gap: 4,
                          padding: '3px 8px', borderRadius: 6,
                          background: '#e0e5ec', fontSize: 11,
                        }}>
                          <FIcon size={12} />
                          <span style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {file.filename}
                          </span>
                          <span style={{ color: '#868e96', fontSize: 9 }}>{formatFileSize(file.size)}</span>
                          <button onClick={() => removePendingAttachment(idx)} style={{
                            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                            color: '#dc3545', display: 'flex',
                          }}>
                            <X size={10} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Input */}
                <div style={{
                  padding: '8px 12px', borderTop: '1px solid #e9ecef',
                  display: 'flex', gap: 8, alignItems: 'center',
                  background: '#fff', borderRadius: '0 0 12px 0',
                }}>
                  {/* Hidden file input (web fallback only) */}
                  {!isElectron && (
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                      accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar,.gz,.json,.xml"
                    />
                  )}
                  {/* Attach button — uses native dialog in Electron, hidden input on web */}
                  <button
                    onClick={() => isElectron ? handleFileSelect() : fileInputRef.current?.click()}
                    title="Attach files or videos (up to 100MB)"
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      padding: '4px', display: 'flex', color: '#6C757D',
                    }}
                  >
                    <Paperclip size={16} />
                  </button>
                  <input
                    value={chatInput}
                    onChange={e => setChatInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendWithAttachments(); } }}
                    placeholder={uploadingFiles ? "Uploading files..." : "Type a secure message..."}
                    disabled={uploadingFiles}
                    style={{
                      flex: 1, padding: '8px 12px', borderRadius: 8, border: 'none',
                      background: '#e0e5ec', boxShadow: neuShadowInset,
                      fontSize: 12, outline: 'none',
                    }}
                  />
                  <button
                    onClick={sendWithAttachments}
                    disabled={chatLoading || (!chatInput.trim() && pendingAttachments.length === 0)}
                    style={{
                      ...btnPrimary, padding: '6px 12px',
                      opacity: chatLoading || (!chatInput.trim() && pendingAttachments.length === 0) ? 0.5 : 1,
                    }}
                  >
                    {uploadingFiles ? <Clock size={14} className="spin" /> : <Send size={14} />}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ═══ Inbox Action Buttons (Dropdown Popouts) ═══ */}
      <div style={{
        display: 'flex', gap: 6, marginBottom: 14,
        flexWrap: 'wrap', position: 'relative',
      }}>
        {INBOX_TABS.map(t => {
          const active = inboxTab === t.id;
          const isOpen = inboxTab === t.id && openDropdown === t.id;
          const Icon = t.icon;
          return (
            <div key={t.id} style={{ position: 'relative' }}>
              <button
                onClick={() => {
                  if (openDropdown === t.id) {
                    setOpenDropdown(null);
                  } else {
                    setInboxTab(t.id);
                    setOpenDropdown(t.id);
                  }
                }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '8px 14px', border: 'none', cursor: 'pointer',
                  borderRadius: 10, fontSize: 12, fontWeight: isOpen ? 700 : 500,
                  background: isOpen ? '#2E8B98' : active ? '#e0f2fe' : '#F8F9FA',
                  color: isOpen ? '#fff' : active ? '#2E8B98' : '#495057',
                  boxShadow: isOpen ? '0 4px 12px rgba(46,139,152,0.3)' : neuShadowSm,
                  transition: 'all 0.2s ease', whiteSpace: 'nowrap',
                }}
                onMouseEnter={e => { if (!isOpen) e.currentTarget.style.background = active ? '#d0ebf5' : '#eef1f5'; }}
                onMouseLeave={e => { if (!isOpen) e.currentTarget.style.background = active ? '#e0f2fe' : '#F8F9FA'; }}
              >
                <Icon size={14} />
                {t.label}
                <span style={{
                  fontSize: 10, fontWeight: 700,
                  background: isOpen ? 'rgba(255,255,255,0.3)' : active ? '#2E8B98' : '#ADB5BD',
                  color: '#fff', padding: '1px 6px', borderRadius: 8, minWidth: 20,
                  textAlign: 'center',
                }}>{t.count}</span>
                <ChevronDown size={12} style={{
                  transition: 'transform 0.2s ease',
                  transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  opacity: 0.7,
                }} />
              </button>

              {/* ─── Dropdown Popout Panel ─── */}
              {isOpen && (
                <InboxDropdownPanel
                  tab={t}
                  interactions={processed}
                  readSet={readSet}
                  starSet={starSet}
                  followedIds={followedIds}
                  onToggleRead={toggleRead}
                  onToggleStar={toggleStar}
                  onClose={() => setOpenDropdown(null)}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* ═══ Search + Filter Toggle Row ═══ */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'stretch' }}>
        {/* Search */}
        <div style={{ position: 'relative', flex: 1 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: 10, color: '#ADB5BD' }} />
          <input
            placeholder="Search by subject, contact, description, or outcome..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%', padding: '8px 32px 8px 30px', borderRadius: 10, border: 'none',
              background: '#e0e5ec', boxShadow: neuShadowInset, fontSize: 13, outline: 'none',
            }}
          />
          {search && (
            <button onClick={() => setSearch('')} style={{
              position: 'absolute', right: 8, top: 7, background: 'none', border: 'none',
              cursor: 'pointer', padding: 2, borderRadius: 4, display: 'flex',
            }}>
              <X size={14} color="#ADB5BD" />
            </button>
          )}
        </div>

        {/* Enhanced search toggle */}
        <button
          onClick={() => setShowEnhancedSearch(s => !s)}
          style={{
            ...btnSm,
            background: showEnhancedSearch ? '#256F7A' : '#F8F9FA',
            color: showEnhancedSearch ? '#fff' : '#6C757D',
            boxShadow: neuShadowSm,
          }}
        >
          <Search size={14} />
          Advanced
          {hasEnhancedSearch && (
            <span style={{
              position: 'absolute', top: -4, right: -4,
              background: '#256F7A', color: '#fff', fontSize: 9, fontWeight: 700,
              width: 16, height: 16, borderRadius: '50%', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
            }}>!</span>
          )}
        </button>

        {/* Filter toggle */}
        <button
          onClick={() => setShowFilters(f => !f)}
          style={{
            ...btnSm,
            background: showFilters ? '#2E8B98' : '#F8F9FA',
            color: showFilters ? '#fff' : '#6C757D',
            boxShadow: neuShadowSm,
            position: 'relative',
          }}
        >
          <SlidersHorizontal size={14} />
          Filters
          {activeFilterCount > 0 && (
            <span style={{
              position: 'absolute', top: -4, right: -4,
              background: '#DC3545', color: '#fff', fontSize: 9, fontWeight: 700,
              width: 16, height: 16, borderRadius: '50%', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
            }}>{activeFilterCount}</span>
          )}
        </button>
      </div>

      {/* ═══ Enhanced Search Panel (collapsible) ═══ */}
      <div style={{
        maxHeight: showEnhancedSearch ? 200 : 0, overflow: 'hidden',
        transition: 'max-height 0.3s ease-in-out',
      }}>
        <div style={{
          background: '#F8F9FA', borderRadius: 12, padding: 14,
          marginBottom: 12, boxShadow: neuShadowSm,
          border: '1px solid #d0e6ed',
        }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginBottom: 10,
          }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#212529', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Search size={13} color="#256F7A" /> Enhanced Search
            </span>
            {hasEnhancedSearch && (
              <button onClick={() => { setDateFrom(''); setDateTo(''); setContactSearch(''); setTagSearch(''); }} style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 11, color: '#DC3545', fontWeight: 600,
              }}>Clear</button>
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
            <div>
              <label style={{ fontSize: 10, fontWeight: 600, color: '#6C757D', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                <Calendar size={10} /> Date From
              </label>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                style={{ ...inp, fontSize: 12, padding: '6px 10px' }} />
            </div>
            <div>
              <label style={{ fontSize: 10, fontWeight: 600, color: '#6C757D', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                <Calendar size={10} /> Date To
              </label>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                style={{ ...inp, fontSize: 12, padding: '6px 10px' }} />
            </div>
            <div>
              <label style={{ fontSize: 10, fontWeight: 600, color: '#6C757D', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                <Users size={10} /> Contact Name
              </label>
              <input placeholder="Search contacts..." value={contactSearch} onChange={e => setContactSearch(e.target.value)}
                style={{ ...inp, fontSize: 12, padding: '6px 10px' }} />
            </div>
            <div>
              <label style={{ fontSize: 10, fontWeight: 600, color: '#6C757D', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                <Tag size={10} /> Tag Search
              </label>
              <input placeholder="Search tags..." value={tagSearch} onChange={e => setTagSearch(e.target.value)}
                style={{ ...inp, fontSize: 12, padding: '6px 10px' }} />
            </div>
          </div>
        </div>
      </div>

      {/* ═══ Advanced Filter Panel (collapsible) ═══ */}
      <div style={{
        maxHeight: showFilters ? 500 : 0, overflow: 'hidden',
        transition: 'max-height 0.3s ease-in-out',
      }}>
        <div style={{
          background: '#F8F9FA', borderRadius: 12, padding: 14,
          marginBottom: 12, boxShadow: neuShadowSm,
          border: '1px solid #e9ecef',
        }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginBottom: 10,
          }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#212529', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Filter size={13} color="#2E8B98" /> Advanced Filters
            </span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {/* Quick presets */}
              {currentUser && (
                <>
                  <button onClick={() => setFilter('assigned', currentUser.displayName || currentUser.username)} style={{
                    background: 'none', border: '1px solid #2E8B98', cursor: 'pointer',
                    fontSize: 10, color: '#2E8B98', fontWeight: 600, padding: '2px 8px',
                    borderRadius: 6, display: 'flex', alignItems: 'center', gap: 3,
                  }}>
                    <UserCheck size={10} /> Assigned to Me
                  </button>
                </>
              )}
              {activeFilterCount > 0 && (
                <button onClick={clearFilters} style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: 11, color: '#DC3545', fontWeight: 600,
                }}>Clear All</button>
              )}
            </div>
          </div>

          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: 10,
          }}>
            {FILTER_DEFS.map(fd => {
              const Icon = fd.icon;
              let opts = fd.options;
              if (fd.key === 'assigned' || fd.key === 'follower' || fd.key === 'mentioned') opts = userOptions;
              if (fd.key === 'tagged') opts = tagOptions;
              return (
                <div key={fd.key}>
                  <label style={{
                    fontSize: 10, fontWeight: 600, color: '#6C757D',
                    display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3,
                    textTransform: 'uppercase', letterSpacing: 0.5,
                  }}>
                    <Icon size={10} /> {fd.label}
                  </label>
                  <select
                    value={filters[fd.key]}
                    onChange={e => setFilter(fd.key, e.target.value)}
                    style={{
                      ...inp, fontSize: 12, padding: '6px 10px',
                      color: filters[fd.key] ? '#212529' : '#ADB5BD',
                    }}
                  >
                    <option value="">Any</option>
                    {opts.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ═══ Stats + Controls ═══ */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 10, padding: '0 2px',
      }}>
        <div style={{ fontSize: 12, color: '#6C757D' }}>
          <strong style={{ color: '#212529' }}>{filteredCount}</strong> conversation{filteredCount !== 1 ? 's' : ''}
          {inboxTab !== 'all' && <span> in <strong>{inboxTab === 'assigned' ? 'assigned to me' : inboxTab === 'followed' ? 'followed by me' : inboxTab}</strong></span>}
          {search && <span> matching "<em>{search}</em>"</span>}
          {activeFilterCount > 0 && <span> · {activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''} active</span>}
          {hasEnhancedSearch && <span> · enhanced search active</span>}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={expandAll} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 11, color: '#2E8B98', fontWeight: 600,
          }}>Expand All</button>
          <span style={{ color: '#DEE2E6' }}>|</span>
          <button onClick={collapseAll} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 11, color: '#2E8B98', fontWeight: 600,
          }}>Collapse All</button>
        </div>
      </div>

      {/* ═══ Conversation Groups ═══ */}
      {filteredCount === 0 ? (
        <div style={emptyMsg}>
          {interactions.length === 0
            ? 'No conversations logged'
            : inboxTab === 'unread' ? 'All caught up — no unread conversations'
            : inboxTab === 'starred' ? 'No starred conversations'
            : inboxTab === 'recent' ? 'No conversations in the last 7 days'
            : inboxTab === 'assigned' ? 'No conversations assigned to you'
            : inboxTab === 'followed' ? 'No followed conversations — click the follow icon on any conversation'
            : `No conversations matching your criteria`}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {typeNames.map(typeName => (
            <TypeGroup
              key={typeName}
              typeName={typeName}
              interactions={grouped[typeName]}
              isExpanded={expandedGroups.has(typeName)}
              onToggle={() => toggleGroup(typeName)}
              readSet={readSet}
              starSet={starSet}
              followedIds={followedIds}
              onToggleRead={toggleRead}
              onToggleStar={toggleStar}
              onToggleFollow={toggleFollow}
              expandedNoteId={expandedNoteId}
              setExpandedNoteId={setExpandedNoteId}
              notesMap={notesMap}
              loadNotes={loadNotes}
              noteInput={noteInput}
              setNoteInput={setNoteInput}
              addNote={addNote}
              currentUser={currentUser}
              allUsers={allUsers}
              onContactClick={onContactClick}
            />
          ))}
        </div>
      )}
    </>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════
   Single type accordion group
   ═══════════════════════════════════════════════════════════════════════════ */

function TypeGroup({
  typeName, interactions, isExpanded, onToggle, readSet, starSet, followedIds,
  onToggleRead, onToggleStar, onToggleFollow,
  expandedNoteId, setExpandedNoteId, notesMap, loadNotes,
  noteInput, setNoteInput, addNote, currentUser, allUsers, onContactClick,
}) {
  const listRef = useRef(null);
  const { onKeyDown: handleListKeyDown } = useArrowNav(listRef, '[data-interaction-item]', { horizontal: false, vertical: true });

  const meta = TYPE_META[typeName] || TYPE_META['Other'];
  const Icon = meta.icon;
  const unreadInGroup = interactions.filter(ix => !readSet.has(ix.id)).length;

  return (
    <div style={{
      background: '#F8F9FA', borderRadius: 12, boxShadow: neuShadowSm,
      overflow: 'hidden', transition: 'all .2s ease',
      border: isExpanded ? `2px solid ${meta.color}` : '2px solid transparent',
    }}>
      {/* ─── Type Header ─── */}
      <button
        onClick={onToggle}
        style={{
          display: 'flex', alignItems: 'center', gap: 10, width: '100%',
          padding: '10px 16px', border: 'none', cursor: 'pointer', textAlign: 'left',
          background: isExpanded ? `${meta.color}08` : '#F8F9FA',
          borderRadius: isExpanded ? '10px 10px 0 0' : 10,
          transition: 'background .15s ease',
        }}
        onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.background = '#f0f4f8'; }}
        onMouseLeave={e => { if (!isExpanded) e.currentTarget.style.background = isExpanded ? `${meta.color}08` : '#F8F9FA'; }}
      >
        <ChevronRight
          size={16}
          color={isExpanded ? meta.color : '#6C757D'}
          style={{
            transition: 'transform .25s ease',
            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
            flexShrink: 0,
          }}
        />
        <Icon size={15} color={meta.color} />
        <span style={{
          fontSize: 15, fontWeight: 700, color: isExpanded ? meta.color : '#212529',
        }}>
          {typeName}
        </span>
        <div style={{ flex: 1 }} />
        {unreadInGroup > 0 && (
          <span style={{
            fontSize: 10, fontWeight: 700, background: '#DC3545', color: '#fff',
            padding: '1px 6px', borderRadius: 8, marginRight: 4,
          }}>{unreadInGroup} new</span>
        )}
        <Badge text={`${interactions.length}`} color={isExpanded ? meta.color : '#94a3b8'} />
      </button>

      {/* ─── Collapsible List ─── */}
      <div style={{
        maxHeight: isExpanded ? 99999 : 0,
        opacity: isExpanded ? 1 : 0,
        overflow: 'hidden',
        transition: 'max-height .35s ease, opacity .25s ease',
      }}>
        <div ref={listRef} onKeyDown={handleListKeyDown} style={{ padding: '0 4px 4px' }}>
          {interactions.map((ix, i) => (
            <InteractionRow
              key={ix.id}
              interaction={ix}
              index={i}
              isRead={readSet.has(ix.id)}
              isStarred={starSet.has(ix.id)}
              isFollowed={followedIds.has(ix.id)}
              onToggleRead={() => onToggleRead(ix.id)}
              onToggleStar={() => onToggleStar(ix.id)}
              onToggleFollow={() => onToggleFollow(ix.id)}
              isNoteExpanded={expandedNoteId === ix.id}
              onToggleNotes={() => {
                if (expandedNoteId === ix.id) {
                  setExpandedNoteId(null);
                } else {
                  setExpandedNoteId(ix.id);
                  loadNotes(ix.id);
                }
              }}
              notes={notesMap[ix.id] || []}
              noteInput={expandedNoteId === ix.id ? noteInput : ''}
              setNoteInput={setNoteInput}
              addNote={() => addNote(ix.id)}
              currentUser={currentUser}
              onContactClick={onContactClick}
            />
          ))}
        </div>
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════
   Single interaction row with notes thread
   ═══════════════════════════════════════════════════════════════════════════ */

function InteractionRow({
  interaction: ix, index: i, isRead, isStarred, isFollowed,
  onToggleRead, onToggleStar, onToggleFollow,
  isNoteExpanded, onToggleNotes, notes, noteInput, setNoteInput, addNote,
  currentUser, onContactClick,
}) {
  const contactName = [ix.contact_first, ix.contact_last].filter(Boolean).join(' ') ||
                     (ix.patient_first || ix.patient_last ? [ix.patient_first, ix.patient_last].filter(Boolean).join(' ') : 'Unknown Contact');

  return (
    <div style={{ marginBottom: 2 }}>
      <div
        data-interaction-item
        tabIndex={0}
        style={{
          display: 'flex', alignItems: 'flex-start', gap: 8,
          padding: '10px 12px',
          background: !isRead ? '#e8f4fd' : (i % 2 === 0 ? '#fff' : '#F8F9FA'),
          borderRadius: isNoteExpanded ? '8px 8px 0 0' : 8,
          outline: 'none',
          transition: 'background .1s ease',
          borderLeft: !isRead ? '3px solid #2E8B98' : '3px solid transparent',
        }}
        onFocus={e => { e.currentTarget.style.backgroundColor = '#dce2e8'; }}
        onBlur={e => { e.currentTarget.style.backgroundColor = !isRead ? '#e8f4fd' : (i % 2 === 0 ? '#fff' : '#F8F9FA'); }}
        onClick={() => { if (!isRead) onToggleRead(); }}
      >
        {/* Star button */}
        <button
          onClick={e => { e.stopPropagation(); onToggleStar(); }}
          style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: 2,
            flexShrink: 0, marginTop: 2,
          }}
          title={isStarred ? 'Unstar' : 'Star'}
        >
          <Star
            size={14}
            color={isStarred ? '#F59E0B' : '#DEE2E6'}
            fill={isStarred ? '#F59E0B' : 'none'}
          />
        </button>

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Subject + Date */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 4 }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <span style={{
                fontWeight: !isRead ? 700 : 600, fontSize: 13,
                color: !isRead ? '#0c4a6e' : '#212529',
              }}>{ix.subject}</span>
              <span
                onClick={() => onContactClick && onContactClick(ix.contact_id || ix.patient_id)}
                style={{
                  fontSize: 11, color: '#2E8B98', marginLeft: 8, cursor: 'pointer', fontWeight: 600,
                  display: (ix.contact_id || ix.patient_id) ? 'inline' : 'none',
                }}
              >
                {contactName}
              </span>
              {!(ix.contact_id || ix.patient_id) && (
                <span style={{ fontSize: 11, color: '#ADB5BD', marginLeft: 8 }}>{contactName}</span>
              )}
            </div>
            <div style={{ fontSize: 11, color: '#ADB5BD', whiteSpace: 'nowrap', marginLeft: 8 }}>
              {ix.interaction_date?.substring(0, 10)}
            </div>
          </div>

          {/* Badges */}
          <div style={{ display: 'flex', gap: 6, fontSize: 11, marginBottom: 4, flexWrap: 'wrap', alignItems: 'center' }}>
            <Badge text={ix.interaction_type.replace(/_/g, ' ')} color="#2E8B98" />
            <Badge text={ix.direction} color={ix.direction === 'inbound' ? '#62BB46' : '#F26522'} />
            {ix.assigned_to_name && (
              <span style={{ fontSize: 10, color: '#6C757D', display: 'flex', alignItems: 'center', gap: 2 }}>
                <UserCheck size={10} /> {ix.assigned_to_name}
              </span>
            )}
            {ix.duration_minutes && (
              <span style={{ color: '#ADB5BD', display: 'flex', alignItems: 'center', gap: 3 }}>
                <Clock size={10} /> {ix.duration_minutes} min
              </span>
            )}
            {!isRead && (
              <span style={{
                fontSize: 9, fontWeight: 700, color: '#2E8B98',
                background: '#e0f2fe', padding: '1px 5px', borderRadius: 4,
              }}>NEW</span>
            )}
          </div>

          {/* Description */}
          {ix.description && (
            <div style={{
              fontSize: 12, color: !isRead ? '#334155' : '#6C757D', marginBottom: 2,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {ix.description}
            </div>
          )}

          {/* Outcome */}
          {ix.outcome && (
            <div style={{ fontSize: 12, color: '#212529' }}>
              <strong>Outcome:</strong> {ix.outcome}
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0, marginTop: 2 }}>
          {/* Follow */}
          <button
            onClick={e => { e.stopPropagation(); onToggleFollow(); }}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 2,
            }}
            title={isFollowed ? 'Unfollow' : 'Follow'}
          >
            <Bell
              size={13}
              color={isFollowed ? '#2E8B98' : '#DEE2E6'}
              fill={isFollowed ? '#2E8B98' : 'none'}
            />
          </button>

          {/* Notes thread */}
          <button
            onClick={e => { e.stopPropagation(); onToggleNotes(); }}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 2,
            }}
            title="Notes & Comments"
          >
            <MessageCircle
              size={13}
              color={isNoteExpanded ? '#2E8B98' : (notes.length > 0 ? '#62BB46' : '#DEE2E6')}
              fill={notes.length > 0 ? (isNoteExpanded ? '#2E8B98' : '#62BB46') : 'none'}
            />
          </button>

          {/* Read/Unread toggle */}
          <button
            onClick={e => { e.stopPropagation(); onToggleRead(); }}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 2,
            }}
            title={isRead ? 'Mark as unread' : 'Mark as read'}
          >
            {isRead
              ? <MailOpen size={13} color="#ADB5BD" />
              : <Mail size={13} color="#2E8B98" />
            }
          </button>
        </div>
      </div>

      {/* ─── Notes/Comments Thread (expandable) ─── */}
      {isNoteExpanded && (
        <div style={{
          background: '#f0f4f8', borderRadius: '0 0 8px 8px',
          padding: '10px 14px', borderTop: '1px solid #e0e5ec',
          borderLeft: !isRead ? '3px solid #2E8B98' : '3px solid transparent',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8,
          }}>
            <MessageCircle size={12} color="#2E8B98" />
            <span style={{ fontSize: 11, fontWeight: 700, color: '#212529' }}>
              Internal Notes & Comments
            </span>
            <Lock size={10} color="#6C757D" />
            <span style={{ fontSize: 9, color: '#ADB5BD' }}>Visible only to team members</span>
          </div>

          {/* Existing notes */}
          {notes.length === 0 && (
            <div style={{ fontSize: 11, color: '#ADB5BD', marginBottom: 8, fontStyle: 'italic' }}>
              No notes yet — be the first to add one
            </div>
          )}
          {notes.map(note => (
            <div key={note.id} style={{
              display: 'flex', gap: 8, marginBottom: 8, padding: '6px 10px',
              background: '#fff', borderRadius: 8, boxShadow: neuShadowSm,
            }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                background: 'linear-gradient(135deg, #2E8B98, #62BB46)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontSize: 11, fontWeight: 700,
              }}>
                {(note.author_name || 'U').charAt(0).toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#212529' }}>
                    {note.author_name || 'Unknown'}
                  </span>
                  <span style={{ fontSize: 10, color: '#ADB5BD' }}>
                    {note.created_at ? new Date(note.created_at).toLocaleString() : ''}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: '#495057', lineHeight: 1.4 }}>
                  {note.note_text}
                </div>
              </div>
            </div>
          ))}

          {/* Add note input */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
            <input
              value={noteInput}
              onChange={e => setNoteInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); addNote(); } }}
              onClick={e => e.stopPropagation()}
              placeholder="Add a note or comment..."
              style={{
                flex: 1, padding: '7px 12px', borderRadius: 8, border: 'none',
                background: '#e0e5ec', boxShadow: neuShadowInset,
                fontSize: 12, outline: 'none',
              }}
            />
            <button
              onClick={e => { e.stopPropagation(); addNote(); }}
              disabled={!noteInput.trim()}
              style={{
                ...btnPrimary, padding: '6px 10px', fontSize: 11,
                opacity: !noteInput.trim() ? 0.5 : 1,
              }}
            >
              <Send size={12} /> Post
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════
   Inbox Dropdown Popout Panel
   Shows a readable list of conversations for the selected inbox category.
   Can be closed via X button or clicking the same tab button again.
   ═══════════════════════════════════════════════════════════════════════════ */

function InboxDropdownPanel({
  tab, interactions, readSet, starSet, followedIds,
  onToggleRead, onToggleStar, onClose,
}) {
  const panelRef = useRef(null);
  const [viewingIx, setViewingIx] = useState(null);
  const [modalNotes, setModalNotes] = useState([]);
  const [modalNoteInput, setModalNoteInput] = useState('');
  const MAX_PREVIEW = 20;
  const items = interactions.slice(0, MAX_PREVIEW);
  const hasMore = interactions.length > MAX_PREVIEW;

  // Load notes when a conversation is opened
  const loadModalNotes = useCallback(async (interactionId) => {
    try {
      const notes = await window.zignagenixAPI.getCRMInteractionNotes(interactionId);
      if (Array.isArray(notes)) setModalNotes(notes);
    } catch { setModalNotes([]); }
  }, []);

  const addModalNote = useCallback(async () => {
    if (!modalNoteInput.trim() || !viewingIx) return;
    try {
      await window.zignagenixAPI.createCRMInteractionNote(viewingIx.id, modalNoteInput.trim());
      setModalNoteInput('');
      await loadModalNotes(viewingIx.id);
    } catch {}
  }, [modalNoteInput, viewingIx, loadModalNotes]);

  const openConversation = useCallback((ix) => {
    setViewingIx(ix);
    setModalNotes([]);
    setModalNoteInput('');
    loadModalNotes(ix.id);
    // Mark as read
    if (!readSet.has(ix.id)) onToggleRead(ix.id);
  }, [readSet, onToggleRead, loadModalNotes]);

  // Close on outside click (only when modal is NOT open)
  useEffect(() => {
    const handler = (e) => {
      if (viewingIx) return; // modal handles its own close
      if (panelRef.current && !panelRef.current.contains(e.target) &&
          !e.target.closest('[data-dropdown-trigger]')) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose, viewingIx]);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') {
        if (viewingIx) setViewingIx(null);
        else onClose();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose, viewingIx]);

  const Icon = tab.icon;

  return (
    <div
      ref={panelRef}
      style={{
        position: 'absolute', top: '100%', left: 0, marginTop: 6,
        width: 420, maxHeight: 480, zIndex: 1000,
        background: '#fff', borderRadius: 14,
        boxShadow: '0 8px 32px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.08)',
        border: '2px solid #2E8B98',
        display: 'flex', flexDirection: 'column',
        animation: 'dropdownFadeIn 0.15s ease-out',
      }}
    >
      {/* Dropdown arrow */}
      <div style={{
        position: 'absolute', top: -7, left: 20,
        width: 12, height: 12, background: '#fff',
        border: '2px solid #2E8B98', borderRight: 'none', borderBottom: 'none',
        transform: 'rotate(45deg)',
      }} />

      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '12px 16px', borderBottom: '1px solid #e9ecef',
        background: '#f8fbfd', borderRadius: '12px 12px 0 0',
      }}>
        <Icon size={16} color="#2E8B98" />
        <span style={{ fontSize: 14, fontWeight: 700, color: '#212529', flex: 1 }}>
          {tab.label}
        </span>
        <span style={{
          fontSize: 11, fontWeight: 600, color: '#2E8B98',
          background: '#e0f2fe', padding: '2px 8px', borderRadius: 8,
        }}>
          {tab.count} conversation{tab.count !== 1 ? 's' : ''}
        </span>
        <button
          onClick={onClose}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: 4, borderRadius: 6, display: 'flex',
            transition: 'background 0.15s ease',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = '#fee2e2'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
          title="Close"
        >
          <X size={16} color="#DC3545" />
        </button>
      </div>

      {/* Conversation list */}
      <div style={{
        flex: 1, overflowY: 'auto', padding: '4px 8px',
      }}>
        {items.length === 0 ? (
          <div style={{
            padding: 24, textAlign: 'center', color: '#ADB5BD', fontSize: 13,
          }}>
            <Icon size={28} color="#DEE2E6" style={{ marginBottom: 8 }} />
            <div>
              {tab.id === 'unread' ? 'All caught up — no unread conversations'
                : tab.id === 'starred' ? 'No starred conversations yet'
                : tab.id === 'recent' ? 'No conversations in the last 7 days'
                : tab.id === 'assigned' ? 'No conversations assigned to you'
                : tab.id === 'followed' ? 'No followed conversations'
                : 'No conversations'}
            </div>
          </div>
        ) : (
          items.map((ix, i) => {
            const contactName = [ix.contact_first, ix.contact_last].filter(Boolean).join(' ') ||
              [ix.patient_first, ix.patient_last].filter(Boolean).join(' ') || 'Unknown';
            const isRead = readSet.has(ix.id);
            const isStarred = starSet.has(ix.id);
            const typeMeta = TYPE_META[(ix.interaction_type || 'other').replace(/_/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')] || TYPE_META['Other'];
            const TypeIcon = typeMeta.icon;

            return (
              <div
                key={ix.id}
                onClick={() => openConversation(ix)}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                  padding: '10px 12px', borderRadius: 8,
                  background: !isRead ? '#f0f7ff' : (i % 2 === 0 ? '#fff' : '#fafbfc'),
                  borderLeft: !isRead ? '3px solid #2E8B98' : '3px solid transparent',
                  marginBottom: 2,
                  transition: 'background 0.1s ease',
                  cursor: 'pointer',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#e8f0f8'; }}
                onMouseLeave={e => { e.currentTarget.style.background = !isRead ? '#f0f7ff' : (i % 2 === 0 ? '#fff' : '#fafbfc'); }}
              >
                {/* Star */}
                <button
                  onClick={(e) => { e.stopPropagation(); onToggleStar(ix.id); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 1, flexShrink: 0, marginTop: 1 }}
                >
                  <Star size={13} color={isStarred ? '#F59E0B' : '#DEE2E6'} fill={isStarred ? '#F59E0B' : 'none'} />
                </button>

                {/* Type icon */}
                <TypeIcon size={14} color={typeMeta.color} style={{ flexShrink: 0, marginTop: 2 }} />

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 2 }}>
                    <span style={{
                      fontSize: 12, fontWeight: !isRead ? 700 : 500,
                      color: !isRead ? '#0c4a6e' : '#212529',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      flex: 1,
                    }}>
                      {ix.subject || 'No subject'}
                    </span>
                    <span style={{ fontSize: 10, color: '#ADB5BD', whiteSpace: 'nowrap', marginLeft: 8 }}>
                      {ix.interaction_date?.substring(0, 10)}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: '#6C757D', marginBottom: 2 }}>
                    {contactName}
                    {ix.assigned_to_name && (
                      <span style={{ marginLeft: 6, color: '#ADB5BD' }}>
                        &rarr; {ix.assigned_to_name}
                      </span>
                    )}
                  </div>
                  {ix.description && (
                    <div style={{
                      fontSize: 11, color: '#868e96', lineHeight: 1.3,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {ix.description}
                    </div>
                  )}
                </div>

                {/* Open indicator */}
                <ChevronRight size={14} color="#ADB5BD" style={{ flexShrink: 0, marginTop: 4 }} />
              </div>
            );
          })
        )}

        {hasMore && (
          <div style={{
            padding: '8px 12px', textAlign: 'center',
            fontSize: 11, color: '#2E8B98', fontWeight: 600,
          }}>
            + {interactions.length - MAX_PREVIEW} more — close to see full list below
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{
        padding: '8px 16px', borderTop: '1px solid #e9ecef',
        background: '#f8fbfd', borderRadius: '0 0 12px 12px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <span style={{ fontSize: 10, color: '#ADB5BD' }}>
          Showing {Math.min(items.length, MAX_PREVIEW)} of {interactions.length}
          {' '}&middot; Click to view full conversation
        </span>
        <button
          onClick={onClose}
          style={{
            padding: '4px 12px', borderRadius: 6, border: '1px solid #DEE2E6',
            background: '#fff', color: '#495057', fontSize: 11,
            fontWeight: 600, cursor: 'pointer',
            transition: 'all 0.15s ease',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = '#f8f9fa'; }}
          onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
        >
          Close
        </button>
      </div>

      {/* ═══ Full Conversation Detail Modal ═══ */}
      {viewingIx && (
        <ConversationModal
          ix={viewingIx}
          isStarred={starSet.has(viewingIx.id)}
          isFollowed={followedIds.has(viewingIx.id)}
          onToggleStar={() => onToggleStar(viewingIx.id)}
          notes={modalNotes}
          noteInput={modalNoteInput}
          setNoteInput={setModalNoteInput}
          addNote={addModalNote}
          onClose={() => setViewingIx(null)}
        />
      )}

      {/* Animation keyframes */}
      <style>{`
        @keyframes dropdownFadeIn {
          from { opacity: 0; transform: translateY(-6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes modalSlideIn {
          from { opacity: 0; transform: scale(0.95) translateY(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════
   Full Conversation Detail Modal
   Shows complete conversation content with all fields, notes, and actions.
   Opens when user clicks a conversation row in the dropdown panel.
   ═══════════════════════════════════════════════════════════════════════════ */

function ConversationModal({
  ix, isStarred, isFollowed, onToggleStar,
  notes, noteInput, setNoteInput, addNote, onClose,
}) {
  const modalRef = useRef(null);
  const contactName = [ix.contact_first, ix.contact_last].filter(Boolean).join(' ') ||
    [ix.patient_first, ix.patient_last].filter(Boolean).join(' ') || 'Unknown Contact';
  const typeName = (ix.interaction_type || 'other').replace(/_/g, ' ');
  const titleCased = typeName.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  const typeMeta = TYPE_META[titleCased] || TYPE_META['Other'];
  const TypeIcon = typeMeta.icon;

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };
    // Delay to avoid the same click that opened the modal from closing it
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handler);
    }, 100);
    return () => { clearTimeout(timer); document.removeEventListener('mousedown', handler); };
  }, [onClose]);

  const DetailRow = ({ label, value, icon: RowIcon, color }) => {
    if (!value) return null;
    return (
      <div style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: '1px solid #f0f2f5' }}>
        <div style={{
          width: 100, flexShrink: 0, fontSize: 11, fontWeight: 600,
          color: '#6C757D', display: 'flex', alignItems: 'center', gap: 5,
          textTransform: 'uppercase', letterSpacing: 0.3,
        }}>
          {RowIcon && <RowIcon size={12} color={color || '#ADB5BD'} />}
          {label}
        </div>
        <div style={{ flex: 1, fontSize: 13, color: '#212529', lineHeight: 1.5 }}>
          {value}
        </div>
      </div>
    );
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.4)', zIndex: 2000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20,
    }}>
      <div
        ref={modalRef}
        style={{
          background: '#fff', borderRadius: 16, width: '100%', maxWidth: 620,
          maxHeight: '85vh', display: 'flex', flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.25), 0 4px 16px rgba(0,0,0,0.1)',
          animation: 'modalSlideIn 0.2s ease-out',
          overflow: 'hidden',
        }}
      >
        {/* ─── Modal Header ─── */}
        <div style={{
          padding: '16px 20px', borderBottom: '2px solid #e9ecef',
          background: `linear-gradient(135deg, ${typeMeta.color}08, ${typeMeta.color}15)`,
          display: 'flex', alignItems: 'flex-start', gap: 12,
        }}>
          <div style={{
            width: 40, height: 40, borderRadius: 12, flexShrink: 0,
            background: typeMeta.color, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <TypeIcon size={20} color="#fff" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#212529', marginBottom: 3, lineHeight: 1.3 }}>
              {ix.subject || 'No subject'}
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <Badge text={titleCased} color={typeMeta.color} />
              <Badge text={ix.direction || 'N/A'} color={ix.direction === 'inbound' ? '#62BB46' : '#F26522'} />
              {ix.duration_minutes && <Badge text={`${ix.duration_minutes} min`} color="#6C757D" />}
              <button
                onClick={onToggleStar}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex' }}
              >
                <Star size={15} color={isStarred ? '#F59E0B' : '#DEE2E6'} fill={isStarred ? '#F59E0B' : 'none'} />
              </button>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: 6, borderRadius: 8, display: 'flex', flexShrink: 0,
              transition: 'background 0.15s ease',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#fee2e2'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
            title="Close"
          >
            <X size={20} color="#DC3545" />
          </button>
        </div>

        {/* ─── Modal Body (scrollable) ─── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 20px' }}>
          {/* Contact & Meta */}
          <div style={{ padding: '12px 0', borderBottom: '1px solid #e9ecef' }}>
            <DetailRow label="Contact" value={contactName} icon={Users} color="#2E8B98" />
            <DetailRow label="Date" value={
              ix.interaction_date
                ? new Date(ix.interaction_date).toLocaleString([], { dateStyle: 'full', timeStyle: 'short' })
                : 'N/A'
            } icon={Calendar} color="#6C757D" />
            <DetailRow label="Assigned" value={ix.assigned_to_name} icon={UserCheck} color="#62BB46" />
            <DetailRow label="Direction" value={
              ix.direction ? (ix.direction === 'inbound' ? 'Inbound (received)' : 'Outbound (sent)') : null
            } icon={ix.direction === 'inbound' ? ArrowDownLeft : ArrowUpRight} color={ix.direction === 'inbound' ? '#62BB46' : '#F26522'} />
            {ix.duration_minutes && (
              <DetailRow label="Duration" value={`${ix.duration_minutes} minutes`} icon={Clock} color="#256F7A" />
            )}
          </div>

          {/* Full Description */}
          {ix.description && (
            <div style={{ padding: '14px 0', borderBottom: '1px solid #e9ecef' }}>
              <div style={{
                fontSize: 11, fontWeight: 600, color: '#6C757D', textTransform: 'uppercase',
                letterSpacing: 0.3, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 5,
              }}>
                <FileText size={12} color="#2E8B98" /> Full Description
              </div>
              <div style={{
                fontSize: 13, color: '#212529', lineHeight: 1.7,
                whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                background: '#f8f9fa', borderRadius: 10, padding: '12px 16px',
                border: '1px solid #e9ecef',
              }}>
                {ix.description}
              </div>
            </div>
          )}

          {/* Outcome */}
          {ix.outcome && (
            <div style={{ padding: '14px 0', borderBottom: '1px solid #e9ecef' }}>
              <div style={{
                fontSize: 11, fontWeight: 600, color: '#6C757D', textTransform: 'uppercase',
                letterSpacing: 0.3, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 5,
              }}>
                <ChevronRight size={12} color="#62BB46" /> Outcome
              </div>
              <div style={{
                fontSize: 13, color: '#212529', lineHeight: 1.7,
                whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                background: '#f0fdf4', borderRadius: 10, padding: '12px 16px',
                border: '1px solid #bbf7d0',
              }}>
                {ix.outcome}
              </div>
            </div>
          )}

          {/* Tags */}
          {ix.tags && ix.tags.length > 0 && (
            <div style={{ padding: '12px 0', borderBottom: '1px solid #e9ecef' }}>
              <div style={{
                fontSize: 11, fontWeight: 600, color: '#6C757D', textTransform: 'uppercase',
                letterSpacing: 0.3, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 5,
              }}>
                <Tag size={12} color="#6C757D" /> Tags
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {ix.tags.map((t, i) => (
                  <span key={i} style={{
                    fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 8,
                    background: '#e0e5ec', color: '#495057',
                  }}>
                    {t.name || t}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Notes & Comments */}
          <div style={{ padding: '14px 0' }}>
            <div style={{
              fontSize: 11, fontWeight: 600, color: '#6C757D', textTransform: 'uppercase',
              letterSpacing: 0.3, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 5,
            }}>
              <MessageCircle size={12} color="#2E8B98" /> Notes & Comments
              {notes.length > 0 && (
                <span style={{
                  fontSize: 10, fontWeight: 700, background: '#2E8B98', color: '#fff',
                  padding: '1px 6px', borderRadius: 8,
                }}>{notes.length}</span>
              )}
            </div>

            {notes.length === 0 && (
              <div style={{
                fontSize: 12, color: '#ADB5BD', fontStyle: 'italic', marginBottom: 10,
                textAlign: 'center', padding: 12,
              }}>
                No notes yet — add one below
              </div>
            )}

            {notes.map(note => (
              <div key={note.id} style={{
                display: 'flex', gap: 10, marginBottom: 8, padding: '10px 14px',
                background: '#f8f9fa', borderRadius: 10, border: '1px solid #e9ecef',
              }}>
                <div style={{
                  width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                  background: 'linear-gradient(135deg, #2E8B98, #62BB46)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontSize: 12, fontWeight: 700,
                }}>
                  {(note.author_name || 'U').charAt(0).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#212529' }}>
                      {note.author_name || 'Unknown'}
                    </span>
                    <span style={{ fontSize: 10, color: '#ADB5BD' }}>
                      {note.created_at ? new Date(note.created_at).toLocaleString() : ''}
                    </span>
                  </div>
                  <div style={{ fontSize: 13, color: '#495057', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                    {note.note_text}
                  </div>
                </div>
              </div>
            ))}

            {/* Add note input */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
              <input
                value={noteInput}
                onChange={e => setNoteInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addNote(); } }}
                placeholder="Add a note or comment..."
                style={{
                  flex: 1, padding: '9px 14px', borderRadius: 10, border: '1px solid #DEE2E6',
                  background: '#fff', fontSize: 13, outline: 'none',
                  transition: 'border-color 0.15s ease',
                }}
                onFocus={e => { e.target.style.borderColor = '#2E8B98'; }}
                onBlur={e => { e.target.style.borderColor = '#DEE2E6'; }}
              />
              <button
                onClick={addNote}
                disabled={!noteInput.trim()}
                style={{
                  ...btnPrimary, padding: '8px 14px', fontSize: 12,
                  opacity: !noteInput.trim() ? 0.5 : 1,
                }}
              >
                <Send size={13} /> Post
              </button>
            </div>
          </div>
        </div>

        {/* ─── Modal Footer ─── */}
        <div style={{
          padding: '12px 20px', borderTop: '2px solid #e9ecef',
          display: 'flex', justifyContent: 'flex-end', gap: 8,
          background: '#f8f9fa',
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 20px', borderRadius: 8, border: '1px solid #DEE2E6',
              background: '#fff', color: '#495057', fontSize: 13,
              fontWeight: 600, cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#f8f9fa'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
